import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Lock, ListChecks, ArrowRight, Sparkles, Mountain, Copy, BookOpen } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PlaybookDetail from "@/components/playbooks/PlaybookDetail";
import PlaybookEditor from "@/components/playbooks/PlaybookEditor";

function MissingBanner() {
  const { t } = useTranslation();
  const perks = [[Plus, "perkOwn"], [Copy, "perkCopy"], [ListChecks, "perkJournal"], [BookOpen, "perkMore"], [Sparkles, "perkAi"], [Mountain, "perkClimb"]];
  return (
    <div data-testid="playbooks-missing-banner" className="rounded-2xl bg-slate-950 text-white p-6 sm:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-center">
      <div>
        <div className="text-xs uppercase tracking-widest text-amber-400 font-semibold">{t("playbooks.missingEyebrow")}</div>
        <div className="mt-1 font-display font-bold text-xl">{t("playbooks.missingTitle")}</div>
        <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-slate-300">
          {perks.map(([Icon, k]) => <li key={k} className="flex items-center gap-2"><Icon size={14} className="text-amber-400" />{t(`playbooks.${k}`)}</li>)}
        </ul>
      </div>
      <Link to="/pricing?src=playbooks" data-testid="playbooks-upgrade-btn" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm h-11 px-5 rounded-lg">
        {t("playbooks.upgrade")} <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function Card({ p, active, onClick }) {
  const { t } = useTranslation();
  return (
    <button type="button" onClick={onClick} disabled={p.locked} data-testid={`playbook-card-${p.id}`}
      className={`text-left rounded-xl border p-4 transition-all ${active ? "border-blue-800 ring-2 ring-blue-800/15 bg-white" : "border-slate-200 bg-white hover:border-slate-300 hover:-translate-y-0.5"} ${p.locked ? "opacity-60 cursor-not-allowed" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 truncate">{p.market || "—"}</span>
        {p.locked ? <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-700"><Lock size={11} />Premium</span>
          : p.is_template ? <span className="text-[10px] font-bold uppercase text-blue-800">{t("playbooks.starter")}</span> : null}
      </div>
      <div className="mt-1 font-semibold text-slate-900">{p.title}</div>
      <div className="mt-1 text-xs text-slate-500 line-clamp-2">{p.locked ? t("playbooks.lockedCard", { n: p.checklist_count }) : p.description}</div>
    </button>
  );
}

function FreeLocked() {
  const { t } = useTranslation();
  return (
    <div className="max-w-xl mx-auto text-center py-16" data-testid="playbooks-free-locked">
      <ListChecks size={40} className="mx-auto text-slate-400" />
      <h1 className="mt-4 font-display font-black text-3xl text-slate-900">{t("playbooks.title")}</h1>
      <p className="mt-3 text-slate-600">{t("playbooks.freeBody")}</p>
      <Link to="/pricing?src=playbooks-free" data-testid="playbooks-free-upgrade-btn" className="mt-6 inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-700 text-white font-semibold h-11 px-5 rounded-lg">{t("playbooks.upgrade")} <ArrowRight size={14} /></Link>
    </div>
  );
}

export default function Playbooks({ embedded = false }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  const [editing, setEditing] = useState(null);
  const [rev, setRev] = useState(0);
  const load = useCallback(() => api.get("/playbooks", { params: { lang } }).then((r) => {
    setD(r.data);
    const all = [...r.data.mine, ...r.data.templates].filter((x) => !x.locked);
    setSel((cur) => (all.some((x) => x.id === cur) ? cur : all[0]?.id || null));
  }), [lang]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  if (d.tier === "free") return <FreeLocked />;
  const premium = d.tier === "premium";
  const save = async (f) => {
    try {
      if (f.id) await api.put(`/playbooks/${f.id}`, f); else await api.post("/playbooks", f);
      toast.success(t("playbooks.saved")); setEditing(null); setRev((r) => r + 1); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-8" data-testid="playbooks-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          {!embedded && <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">{t("playbooks.eyebrow")}</div>}
          {!embedded && <h1 className="mt-1 font-display font-black text-4xl text-slate-900">{t("playbooks.title")}</h1>}
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">{t("playbooks.intro")}</p>
        </div>
        {premium && <Button onClick={() => setEditing({})} data-testid="playbooks-new-btn" className="bg-blue-800 hover:bg-blue-700 text-white gap-2"><Plus size={14} />{t("playbooks.new")}</Button>}
      </div>
      {!premium && <MissingBanner />}
      {d.mine.length > 0 && (
        <div><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">{t("playbooks.mine")}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{d.mine.map((p) => <Card key={p.id} p={p} active={sel === p.id} onClick={() => setSel(p.id)} />)}</div></div>
      )}
      <div><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">{t("playbooks.starters")}</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{d.templates.map((p) => <Card key={p.id} p={p} active={sel === p.id} onClick={() => setSel(p.id)} />)}</div></div>
      {sel && <PlaybookDetail key={`${sel}-${rev}-${lang}`} id={sel} premium={premium} isAdmin={d.is_admin} onEdit={(p) => setEditing(p)} onChanged={load} />}
      {editing && <PlaybookEditor open initial={editing.id ? editing : null} isAdmin={d.is_admin} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
